import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api";
import { useMsal } from "@azure/msal-react";

function L3ApprovalReport() {
  const { expenseId, vendorId } = useParams();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();

  useEffect(() => {
    const fetchRecord = async () => {
      try {
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: ["User.Read"],
          account: accounts[0]
        });
        const encodedId = encodeURIComponent(encodeURIComponent(expenseId));
        
        // If vendorId is provided, fetch specific vendor details
        if (vendorId) {
          const res = await api.get(`/api/payments/all-vendors/${encodedId}`, {
            headers: { Authorization: `Bearer ${tokenResponse.accessToken}` }
          });
          const specificVendor = res.data.find(v => v.id.toString() === vendorId);
          if (specificVendor) {
            setRecord(specificVendor);
          } else {
            console.error(`Vendor with ID ${vendorId} not found`);
          }
        } else {
          // Fallback to original logic for backward compatibility
          const res = await api.get(`/api/payments/sent-to-payment/${encodedId}`, {
            headers: { Authorization: `Bearer ${tokenResponse.accessToken}` }
          });
          setRecord(res.data);
        }
        setLoading(false);
      } catch (e) {
        console.error("Error fetching L3 approval record:", e);
        setLoading(false);
      }
    };
    fetchRecord();
  }, [expenseId, vendorId, instance, accounts]);

  const handleAction = async (action) => {
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ["User.Read"],
        account: accounts[0]
      });
      const encodedId = encodeURIComponent(encodeURIComponent(expenseId));
      
      // Include vendor_id in the L3 approval request if available
      const requestBody = { action, comment };
      if (vendorId) {
        requestBody.vendor_id = vendorId;
      }
      
      await api.post(
        `/api/payments/l3-approval/${encodedId}`,
        requestBody,
        { headers: { Authorization: `Bearer ${tokenResponse.accessToken}` } }
      );
      
      const vendorInfo = vendorId ? ` for vendor ${record?.vendor_name}` : '';
      alert(`Expense ${action}${vendorInfo} successfully!`);
      navigate("/sent-to-payment");
    } catch (err) {
      console.error(err);
      alert("Something went wrong!");
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (!record) return <div className="p-6 text-red-600">No record found</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">L3 Approval Report</h2>

      <div className="shadow rounded-xl p-6 space-y-4 border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
        <p><strong>Expense ID:</strong> {record.expense_id}</p>
        <p><strong>Vendor:</strong> {record.vendor_name}</p>
        <p><strong>Approved Amount:</strong> ₹{record.cost}</p>
        <p><strong>Date:</strong> {record.date ? new Date(record.date).toLocaleDateString('en-IN') : 'Invalid Date'}</p>
        <p><strong>Actual Amount:</strong> {record.actual_paid ? `₹${record.actual_paid}` : "-"}</p>
        <p>
          <strong>Attachment:</strong>{" "}
          {record.attachment_url ? (
            <a
              href={record.attachment_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              View Original Receipt/Invoice
            </a>
          ) : record.actual_attachment_url ? (
            <a
              href={record.actual_attachment_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              View Actual Payment Attachment
            </a>
          ) : (
            "No file"
          )}
        </p>

        {/* Comment box */}
        <textarea
          className="w-full border rounded-md p-2"
          placeholder="Add comments here..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />

        {/* Buttons */}
        <div className="flex space-x-4">
          <button
            onClick={() => handleAction("approve")}
            className="px-4 py-2 bg-green-200 text-green-700 rounded-lg hover:bg-green-300"
          >
            Approve
          </button>
          <button
            onClick={() => handleAction("reject")}
            className="px-4 py-2 bg-red-200 text-red-700 rounded-lg hover:bg-red-300"
          >
            Reject
          </button>
        </div>
      </div>
    </div>
  );
}

export default L3ApprovalReport;
